/**
 * ContactPage — premium "Contact us" page reachable at /contact.
 *
 * Shares the PremiumLanding design language (deep ink + parchment +
 * emerald + copper) and includes a smaller, contained version of the
 * 3D housing scene as a side panel — instead of the full-bleed scene
 * used on the homepage, here it sits beside the contact card.
 */

import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useHousingScene } from '../components/useHousingScene';
import LanguageSwitcher from '../components/LanguageSwitcher';
import '../components/PremiumLanding.css';
import './ContactPage.css';

const CONTACT_EMAIL = 'contact@rentnova.app';

export default function ContactPage() {
    const { t } = useTranslation();
    const mailto = `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(t('contact.mailSubject'))}`;

    const sceneCanvasRef = useRef<HTMLCanvasElement | null>(null);
    const sceneState = useHousingScene(sceneCanvasRef, { fitToParent: true });
    const cursorRef = useRef<HTMLDivElement | null>(null);

    // Slow continuous "scroll" so the model gently rotates on the contact page
    useEffect(() => {
        let raf = 0;
        let stopped = false;
        let p = 0;
        const tick = () => {
            if (stopped) return;
            p = (p + 0.0008) % 1;
            sceneState.current.scrollProgress = p;
            raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
        return () => { stopped = true; cancelAnimationFrame(raf); };
    }, [sceneState]);

    // Cursor glow
    useEffect(() => {
        const cursor = cursorRef.current;
        if (!cursor) return;

        let mx = window.innerWidth / 2;
        let my = window.innerHeight / 2;
        let cx = mx, cy = my;
        let raf = 0;
        let stopped = false;

        const setPos = (x: number, y: number) => {
            cursor.style.transform = `translate(${x}px, ${y}px) translate(-50%, -50%)`;
        };
        const onMove = (e: MouseEvent) => {
            mx = e.clientX; my = e.clientY;
            cx = cx + (mx - cx) * 0.5;
            cy = cy + (my - cy) * 0.5;
            setPos(cx, cy);
            sceneState.current.pointerX = (mx / window.innerWidth) * 2 - 1;
            sceneState.current.pointerY = (my / window.innerHeight) * 2 - 1;
        };
        const tick = () => {
            if (stopped) return;
            cx += (mx - cx) * 0.18;
            cy += (my - cy) * 0.18;
            setPos(cx, cy);
            raf = requestAnimationFrame(tick);
        };
        window.addEventListener('mousemove', onMove);
        raf = requestAnimationFrame(tick);
        return () => {
            stopped = true;
            cancelAnimationFrame(raf);
            window.removeEventListener('mousemove', onMove);
        };
    }, [sceneState]);

    // Hover targets bind to cursor expansion
    useEffect(() => {
        const cursor = cursorRef.current;
        if (!cursor) return;
        const els = document.querySelectorAll<HTMLElement>(
            '.premium-landing a, .premium-landing button, ' +
            '.premium-landing .pl-info-card'
        );
        const enter = () => cursor.classList.add('is-hover');
        const leave = () => cursor.classList.remove('is-hover');
        els.forEach((el) => {
            el.addEventListener('mouseenter', enter);
            el.addEventListener('mouseleave', leave);
        });
        return () => {
            els.forEach((el) => {
                el.removeEventListener('mouseenter', enter);
                el.removeEventListener('mouseleave', leave);
            });
        };
    });

    // Magnetic CTAs
    useEffect(() => {
        const root = document.querySelector<HTMLElement>('.premium-landing');
        if (!root) return;
        const magnets = root.querySelectorAll<HTMLElement>('.pl-magnetic');
        const cleanup: Array<() => void> = [];
        magnets.forEach((btn) => {
            const move = (e: MouseEvent) => {
                const r = btn.getBoundingClientRect();
                const x = e.clientX - r.left - r.width / 2;
                const y = e.clientY - r.top - r.height / 2;
                btn.style.transform = `translate(${x * 0.25}px, ${y * 0.25}px)`;
            };
            const leave = () => { btn.style.transform = ''; };
            btn.addEventListener('mousemove', move);
            btn.addEventListener('mouseleave', leave);
            cleanup.push(() => {
                btn.removeEventListener('mousemove', move);
                btn.removeEventListener('mouseleave', leave);
            });
        });
        return () => cleanup.forEach((f) => f());
    }, []);

    return (
        <div className="premium-landing contact-page">
            <div className="pl-bg-tint" />
            <div className="pl-bg-vignette" />
            <div ref={cursorRef} className="pl-cursor-glow" />

            <div className="pl-content">
                {/* ============================== NAV ============================== */}
                <header className="pl-nav">
                    <div className="pl-nav-inner">
                        <Link to="/" className="pl-brand">
                            <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
                                stroke="currentColor" strokeWidth="1.6"
                                strokeLinecap="round" strokeLinejoin="round">
                                <path d="M3 11.5 12 4l9 7.5" />
                                <path d="M5 10v10h14V10" />
                                <path d="M10 20v-5h4v5" />
                            </svg>
                            <span>RentNova</span>
                        </Link>
                        <nav className="pl-nav-links">
                            <Link to="/">{t('marketing.navbar.features')}</Link>
                            <Link to="/">{t('marketing.navbar.howItWorks')}</Link>
                            <Link to="/">{t('marketing.navbar.pricing')}</Link>
                            <Link to="/contact" className="is-active">
                                {t('marketing.navbar.contact')}
                            </Link>
                        </nav>
                        <div className="pl-nav-right">
                            <LanguageSwitcher variant="navbar" />
                            <Link to="/login" className="pl-login">{t('marketing.navbar.login')}</Link>
                            <Link to="/register" className="pl-btn pl-btn-primary pl-magnetic">
                                {t('marketing.navbar.startFree')}
                            </Link>
                        </div>
                    </div>
                </header>

                {/* ============================== HERO ============================== */}
                <section className="contact-hero">
                    <div className="contact-hero-inner">
                        {/* Left: contact content */}
                        <div className="contact-card-col">
                            <div className="pl-eyebrow">
                                <span className="pl-pulse" />
                                {t('contact.eyebrow')}
                            </div>
                            <h1 className="pl-display contact-title">
                                {t('contact.headline').replace(/\.$/, '')}
                                <em>.</em>
                            </h1>
                            <p className="pl-lede">{t('contact.subhead')}</p>

                            <div className="contact-card pl-tilt">
                                <div className="contact-card-inner">
                                    <div className="contact-icon-bubble">
                                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
                                            stroke="currentColor" strokeWidth="1.7">
                                            <rect x="3" y="5" width="18" height="14" rx="2" />
                                            <path d="m3 7 9 6 9-6" />
                                        </svg>
                                    </div>
                                    <div className="contact-card-eyebrow">{t('contact.eyebrow').toUpperCase()}</div>
                                    <a href={mailto} className="contact-email">{CONTACT_EMAIL}</a>
                                    <a href={mailto} className="pl-btn pl-btn-primary pl-btn-xl pl-magnetic">
                                        {t('contact.cta')} →
                                    </a>
                                </div>
                            </div>
                        </div>

                        {/* Right: contained 3D housing scene */}
                        <div className="contact-scene-col">
                            <div className="contact-scene-card">
                                <div className="contact-scene-canvas-wrap">
                                    <canvas ref={sceneCanvasRef} className="contact-scene-canvas" />
                                </div>
                                <div className="contact-scene-meta">
                                    <div className="contact-scene-dot">
                                        <span className="pl-pulse" />
                                        Built in Bratislava
                                    </div>
                                    <div className="contact-scene-tag">
                                        Crafted by a small team for small landlords. Real humans on the other side.
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* ============================== INFO ============================== */}
                <section className="pl-section contact-info-section">
                    <div className="pl-section-inner">
                        <div className="contact-info-grid">
                            <article className="pl-info-card pl-tilt">
                                <div className="pl-info-icon pl-icon-emerald">
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
                                        stroke="currentColor" strokeWidth="1.6">
                                        <circle cx="12" cy="12" r="9" />
                                        <path d="M12 7v5l3 2" />
                                    </svg>
                                </div>
                                <h3>{t('contact.responseTimeTitle')}</h3>
                                <p>{t('contact.responseTimeBody')}</p>
                            </article>
                            <article className="pl-info-card pl-tilt">
                                <div className="pl-info-icon pl-icon-copper">
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
                                        stroke="currentColor" strokeWidth="1.6">
                                        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                                    </svg>
                                </div>
                                <h3>{t('contact.whatToIncludeTitle')}</h3>
                                <p>{t('contact.whatToIncludeBody')}</p>
                            </article>
                            <article className="pl-info-card pl-tilt">
                                <div className="pl-info-icon pl-icon-jade">
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
                                        stroke="currentColor" strokeWidth="1.6">
                                        <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                                        <circle cx="8.5" cy="7" r="4" />
                                        <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
                                        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                                    </svg>
                                </div>
                                <h3>{t('contact.salesTitle')}</h3>
                                <p>{t('contact.salesBody')}</p>
                            </article>
                        </div>

                        <div className="contact-back">
                            <Link to="/" className="pl-back-link">{t('contact.backHome')}</Link>
                        </div>
                    </div>
                </section>

                {/* ============================ FOOTER ============================ */}
                <footer className="pl-footer">
                    <div className="pl-footer-inner">
                        <div className="pl-foot-brand">
                            <Link to="/" className="pl-brand">
                                <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
                                    stroke="currentColor" strokeWidth="1.6"
                                    strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M3 11.5 12 4l9 7.5" />
                                    <path d="M5 10v10h14V10" />
                                    <path d="M10 20v-5h4v5" />
                                </svg>
                                <span>RentNova</span>
                            </Link>
                            <p>{t('footer.tagline')}</p>
                            <a className="pl-foot-mail" href={`mailto:${CONTACT_EMAIL}`}>
                                ✉ {CONTACT_EMAIL}
                            </a>
                        </div>
                        <div className="pl-foot-col">
                            <div className="pl-foot-h">{t('footer.product').toUpperCase()}</div>
                            <Link to="/">{t('footer.features')}</Link>
                            <Link to="/">{t('footer.pricing')}</Link>
                            <Link to="/">{t('footer.integrations')}</Link>
                            <Link to="/">{t('footer.changelog')}</Link>
                        </div>
                        <div className="pl-foot-col">
                            <div className="pl-foot-h">{t('footer.company').toUpperCase()}</div>
                            <Link to="/contact">{t('footer.about')}</Link>
                            <Link to="/contact">{t('footer.blog')}</Link>
                            <Link to="/contact">{t('footer.careers')}</Link>
                            <Link to="/contact">{t('footer.contactUs')}</Link>
                        </div>
                        <div className="pl-foot-col">
                            <div className="pl-foot-h">{t('footer.legal').toUpperCase()}</div>
                            <Link to="/contact">{t('footer.privacy')}</Link>
                            <Link to="/contact">{t('footer.terms')}</Link>
                            <Link to="/contact">{t('footer.cookies')}</Link>
                        </div>
                    </div>
                    <div className="pl-foot-bottom">{t('footer.copyright')} · Crafted with care</div>
                </footer>
            </div>
        </div>
    );
}
